import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  applyTenantCssVars,
  resolveTenant,
  type TenantConfig,
} from '@/config/tenants';

const TenantContext = createContext<TenantConfig | null>(null);

function ensureTenantFontLink(tenant: TenantConfig) {
  const id = `tenant-font-${tenant.id}`;
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (tenant.fonts.source.kind !== 'google') {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = tenant.fonts.source.href;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const tenant = useMemo(
    () =>
      resolveTenant({
        search: location.search,
        envTenant: import.meta.env.VITE_TENANT as string | undefined,
      }),
    [location.search]
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-tenant', tenant.id);
    applyTenantCssVars(tenant);
    ensureTenantFontLink(tenant);

    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) favicon.setAttribute('href', tenant.logo.favicon);

    document.title = tenant.seo.title;
  }, [tenant]);

  return (
    <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantConfig {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    return resolveTenant();
  }
  return ctx;
}
